/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button, ButtonProps, cn } from "@farmlands/shadcn-ui";
import { Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { FC, type JSX, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Modal from "../../../../components/Modal";
import TestIds from "../../../../testIds";
import AccountSwitcher from "../../../azure-b2c/components/AccountSwitcher";
import {
  selectAllFarmerAccounts,
  selectCurrentAccountNumber,
  selectIsCEUser,
} from "../../../azure-b2c/storage";

type SelectedAccountVariant = "default" | "inverse";

const SELECTED_ACCOUNT_VARIANT_STYLES: Record<
  SelectedAccountVariant,
  {
    container?: string;
    accountName?: string;
    buttonVariant: ButtonProps["variant"];
    buttonClassName?: string;
  }
> = {
  default: {
    buttonVariant: "link-inline",
  },
  inverse: {
    container: "flex flex-col items-end",
    accountName: "text-base leading-6 text-[#eaefe6]",
    buttonVariant: "link-inline-inverse",
    buttonClassName: "leading-4",
  },
};

export interface ISelectedAccountProps {
  enableAccountSwitcher?: boolean;
  fullScreenModal?: boolean;
  truncateAccountName?: boolean;
  variant?: SelectedAccountVariant;
  onSwitchAccountClick?: () => void;
}

const SelectedAccount: FC<ISelectedAccountProps> = ({
  enableAccountSwitcher,
  fullScreenModal,
  truncateAccountName,
  variant = "default",
  onSwitchAccountClick,
}: ISelectedAccountProps): JSX.Element | undefined => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const selectedAccountNumber = useSelector(selectCurrentAccountNumber);
  const allAccounts = useSelector(selectAllFarmerAccounts);
  const selectedAccount = Object.values(allAccounts?.accounts ?? {}).find(
    (account) => account.accountNumber === selectedAccountNumber
  );

  const farmlandsAccounts = useSelector(selectAllFarmerAccounts);
  const isCEUser = useSelector(selectIsCEUser);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const {
    container,
    accountName: accountNameClassName,
    buttonVariant,
    buttonClassName,
  } = SELECTED_ACCOUNT_VARIANT_STYLES[variant];

  const canSwitchAccount = useMemo(
    (): boolean =>
      !!(enableAccountSwitcher && farmlandsAccounts?.accounts) &&
      !isCEUser &&
      Object.keys(farmlandsAccounts.accounts).length > 1,
    [enableAccountSwitcher, farmlandsAccounts?.accounts]
  );

  const accountName = useMemo((): JSX.Element | null => {
    if (!selectedAccount?.accountName) {
      return null;
    }

    const accountNameDisplay: JSX.Element = (
      <div
        className={cn("text-xs leading-sm font-semibold text-neutral-800", accountNameClassName)}
        data-testid={TestIds.feat.account.userAccount.selectedAccount.accountName}
      >
        {selectedAccount?.accountName}
      </div>
    );

    if (truncateAccountName && isMd) {
      return (
        <Tooltip
          title={selectedAccount?.accountName}
          className="max-w-[11.25rem] truncate"
          placement="top-end"
          enterDelay={1200}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -12],
                  },
                },
              ],
            },
          }}
        >
          {accountNameDisplay}
        </Tooltip>
      );
    }

    return accountNameDisplay;
  }, [selectedAccount?.accountName, isMd, truncateAccountName, accountNameClassName]);

  if (selectedAccount) {
    return (
      <div
        className={cn("fl-selected-account", container)}
        data-testid={TestIds.feat.account.userAccount.selectedAccount.component}
      >
        {accountName}

        {canSwitchAccount && (
          <>
            <Button
              variant={buttonVariant}
              data-testid={TestIds.feat.account.userAccount.selectedAccount.switchAccount}
              onClick={() => {
                setOpenModal(true);
                onSwitchAccountClick?.();
              }}
              className={cn("text-2xs underline", buttonClassName)}
            >
              Switch account
            </Button>

            {openModal && (
              <Modal
                title="Accounts"
                open
                handleClose={() => setOpenModal(false)}
                fullScreen={fullScreenModal}
                testId={TestIds.feat.account.userAccount.selectedAccount.switchAccountModal}
              >
                <AccountSwitcher closeAccountSwitcher={() => setOpenModal(false)} />
              </Modal>
            )}
          </>
        )}
      </div>
    );
  }
};

export default SelectedAccount;
